/**
 * CheckoutSuccessPage
 * Thank you page after successful payment
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  AppBar,
  Toolbar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Email as EmailIcon,
  CalendarMonth as CalendarIcon,
  Upload as UploadIcon,
  Rocket as RocketIcon,
} from '@mui/icons-material';
import { useSearchParams, Link } from 'react-router-dom';
import { useCartStore } from '../store/cart';
import { trackCheckoutSuccess, trackPageView } from '../lib/analytics';
import api from '../services/api';
import { formatPrice } from '../lib/catalog';

interface SessionData {
  id: string;
  customer_email: string;
  amount_total: number;
  payment_status: string;
  metadata?: {
    order_id?: string;
    company?: string;
    items?: string;
  };
}

const CheckoutSuccessPage: React.FC = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<SessionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const clear = useCartStore(state => state.clear);

  useEffect(() => {
    trackPageView('checkout_success');
    
    // Clear cart after successful purchase
    clear();

    // Fetch session details
    if (sessionId) {
      fetchSessionData();
    } else {
      setLoading(false);
    }
  }, [sessionId, clear]);

  const fetchSessionData = async () => {
    try {
      const response = await api.get(`/checkout/session/${sessionId}`);
      if (response.data.success) {
        setSession(response.data.data);
        
        // Track success
        trackCheckoutSuccess(
          sessionId!,
          response.data.data.metadata?.order_id || sessionId!,
          [],
          response.data.data.amount_total
        );
      }
    } catch (err) {
      console.error('Failed to fetch session:', err);
      setError('Could not load order details. Your payment was still processed successfully.');
    } finally {
      setLoading(false);
    }
  };

  const nextSteps = [
    {
      icon: <EmailIcon />,
      title: 'Check Your Email',
      description: 'We\'ve sent a receipt and welcome email with your account credentials and onboarding instructions',
    },
    {
      icon: <CalendarIcon />,
      title: 'Book Your Kickoff Call',
      description: 'Schedule your project kickoff with our engineering team to discuss requirements and timeline',
      action: {
        label: 'Schedule Now',
        href: 'https://calendly.com/redex-engineering/kickoff',
      },
    },
    {
      icon: <UploadIcon />,
      title: 'Prepare Your Assets',
      description: 'Gather logos, brand guidelines, content, and any existing system documentation',
    },
    {
      icon: <RocketIcon />,
      title: 'Access Your Dashboard',
      description: 'Track project progress, view deliverables, and communicate with your team',
      action: {
        label: 'Go to Dashboard',
        href: '/leadforge/login',
      },
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              color: 'primary.main',
              fontWeight: 'bold',
            }}
          >
            REDEX
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 8 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {/* Success Header */}
            <Box textAlign="center" sx={{ mb: 6 }}>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.05)' },
                    '100%': { transform: 'scale(1)' },
                  },
                }}
              >
                <SuccessIcon sx={{ fontSize: 60, color: 'success.main' }} />
              </Box>
              
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Welcome to REDEX!
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                Your payment has been confirmed
              </Typography>
              <Typography color="text.secondary">
                We're excited to start building your enterprise solution. Our engineering team will reach out within 24 hours.
              </Typography>
            </Box>

            {error && (
              <Alert severity="warning" sx={{ mb: 4 }}>
                {error}
              </Alert>
            )}

            {/* Order Details */}
            {session && (
              <Paper sx={{ p: 4, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Order Details
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Order ID:</Typography>
                    <Typography fontFamily="monospace">
                      {session.metadata?.order_id || session.id.slice(-12).toUpperCase()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Email:</Typography>
                    <Typography>{session.customer_email}</Typography>
                  </Box>
                  
                  {session.metadata?.company && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="text.secondary">Company:</Typography>
                      <Typography>{session.metadata.company}</Typography>
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Amount Paid:</Typography>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {formatPrice(session.amount_total)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Next Steps */}
            <Paper sx={{ p: 4, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <RocketIcon color="primary" />
                <Typography variant="h6">Recommended Next Steps</Typography>
              </Box>
              
              <List disablePadding>
                {nextSteps.map((step, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      px: 0,
                      py: 2,
                      borderBottom: index < nextSteps.length - 1 
                        ? `1px solid ${theme.palette.divider}` 
                        : 'none',
                    }}
                  >
                    <ListItemIcon sx={{ color: 'primary.main' }}>
                      {step.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={step.title}
                      secondary={step.description}
                    />
                    {step.action && (
                      <Button
                        variant="outlined"
                        size="small"
                        href={step.action.href}
                        target="_blank"
                        rel="noopener"
                      >
                        {step.action.label}
                      </Button>
                    )}
                  </ListItem>
                ))}
              </List>
            </Paper>

            {/* Additional Information */}
            <Paper 
              sx={{ 
                p: 3, 
                mb: 4, 
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                border: '1px solid',
                borderColor: 'primary.light',
              }}
            >
              <Typography variant="h6" gutterBottom color="primary">
                What Happens Next?
              </Typography>
              <Typography color="text.secondary" paragraph>
                <strong>Within 24 hours:</strong> Our engineering team will reach out to schedule your kickoff call and begin gathering requirements.
              </Typography>
              <Typography color="text.secondary" paragraph>
                <strong>Within 48 hours:</strong> You'll receive access to your customer dashboard where you can track progress, view deliverables, and communicate with your team.
              </Typography>
              <Typography color="text.secondary">
                <strong>Questions?</strong> Contact us at{' '}
                <a href="mailto:admin@redexct.com" style={{ color: theme.palette.primary.main }}>
                  admin@redexct.com
                </a>{' '}
                or call{' '}
                <a href="tel:+14754551907" style={{ color: theme.palette.primary.main }}>
                  +1 (475) 455-1907
                </a>
              </Typography>
            </Paper>

            {/* Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                component="a"
                href="/leadforge/login"
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #63408b 100%)',
                  },
                }}
              >
                Access Dashboard
              </Button>
              <Button
                variant="outlined"
                component="a"
                href="/#contact"
                size="large"
              >
                Contact Support
              </Button>
              <Button
                variant="outlined"
                component={Link}
                to="/pricing.html"
                size="large"
              >
                View More Services
              </Button>
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
};

export default CheckoutSuccessPage;
